import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
import { getRouteLocaleVariant, normalizeLocale } from 'src/i18n/config'
import LINKS from 'src/_helpers/links'

const ROUTE_VARIANT_PREFIXES = {
  pl: '/pl',
  en: '/en',
}

const getEntitySlugLocales = (entity) => {
  return [
    ...new Set(
      Object.keys(entity?.slugs || {})
        .map((locale) => normalizeLocale(locale))
        .filter(Boolean),
    ),
  ]
}

const getEntitySlugByLocale = (entity, locale) => {
  const normalizedLocale = normalizeLocale(locale)

  if (!normalizedLocale || !entity?.slugs || typeof entity.slugs !== 'object') {
    return null
  }

  const match = Object.entries(entity.slugs).find(
    ([slugLocale, slug]) => normalizeLocale(slugLocale) === normalizedLocale && Boolean(slug),
  )

  return match?.[1] || null
}

export const getLocalizedRouteTemplate = (routeMap, locale) => {
  const normalizedLocale = normalizeLocale(locale)

  if (normalizedLocale && typeof routeMap?.[normalizedLocale] === 'string') {
    return routeMap[normalizedLocale]
  }

  const variant = getRouteLocaleVariant(locale)
  const template = routeMap?.[variant] || routeMap?.en || routeMap?.pl || null

  if (!template) {
    return null
  }

  if (variant === locale) {
    return template
  }

  const variantPrefix = ROUTE_VARIANT_PREFIXES[variant]

  if (!variantPrefix) {
    return template
  }

  if (template === variantPrefix) {
    return `/${locale}`
  }

  if (template.startsWith(`${variantPrefix}/`)) {
    return `/${locale}${template.slice(variantPrefix.length)}`
  }

  return template
}

export const buildLocalizedPath = (routeMap, locale, params = {}) => {
  let template = getLocalizedRouteTemplate(routeMap, locale)

  if (!template) {
    return null
  }

  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') {
      return null
    }

    template = template.replace(`:${key}`, String(value))
  }

  return template
}

export const getEntityTranslationLocales = (entity, { requireSlug = false } = {}) => {
  const localesFromSlugs = requireSlug ? getEntitySlugLocales(entity) : []

  const localesFromTranslations = [
    ...new Set(
      Object.entries(entity?.translations || {})
        .filter(([, translation]) => {
          if (!translation) {
            return false
          }

          return requireSlug ? Boolean(translation.slug) : true
        })
        .map(([locale]) => normalizeLocale(locale))
        .filter(Boolean),
    ),
  ]

  return [...new Set([...localesFromSlugs, ...localesFromTranslations])]
}

export const getCommonTranslationLocales = (...entities) => {
  const localeSets = entities
    .map((item) => {
      if (!item) {
        return []
      }

      if (item.entity) {
        return getEntityTranslationLocales(item.entity, { requireSlug: item.requireSlug })
      }

      return getEntityTranslationLocales(item)
    })
    .filter((items) => items.length > 0)

  if (localeSets.length === 0) {
    return []
  }

  return localeSets[0].filter((locale) => localeSets.every((items) => items.includes(locale)))
}

export const buildLanguageSwitcherOptions = ({
  locale,
  token = null,
  availableLocales = [],
  allowedLocales = null,
  resolvePath,
}) => {
  const options = {}
  const normalizedCurrentLocale = normalizeLocale(locale)
  const normalizedAllowedLocales = Array.isArray(allowedLocales)
    ? allowedLocales.map((targetLocale) => normalizeLocale(targetLocale)).filter(Boolean)
    : null

  availableLocales
    .filter((targetLocale) => {
      const normalizedTargetLocale = normalizeLocale(targetLocale)

      if (!normalizedTargetLocale || normalizedTargetLocale === normalizedCurrentLocale) {
        return false
      }

      return !normalizedAllowedLocales || normalizedAllowedLocales.includes(normalizedTargetLocale)
    })
    .forEach((targetLocale) => {
      const path = resolvePath(targetLocale)

      if (!path) {
        return
      }

      options[targetLocale] = `${path}${token ? `?token=${token}` : ''}`
    })

  return options
}

export const serializeLanguageSwitcherOptions = (options) => {
  return JSON.stringify(options || {})
}

export const getLocalizedEntitySlug = (entity, locale, { fallbackSlug = null } = {}) => {
  const directSlug =
    getEntitySlugByLocale(entity, locale) ||
    getPreferredTranslation(entity, locale, {
      fallbackLocale: null,
      includeDefaultLang: false,
      includeFirstTranslation: false,
      defaultValue: null,
    })?.slug

  if (directSlug) {
    return directSlug
  }

  const defaultLangSlug = entity?.defaultLang?.value
    ? getEntitySlugByLocale(entity, entity.defaultLang.value) ||
      getPreferredTranslation(entity, entity.defaultLang.value, {
        fallbackLocale: null,
        includeDefaultLang: false,
        includeFirstTranslation: false,
        defaultValue: null,
      })?.slug
    : null

  return defaultLangSlug || entity?.slug || fallbackSlug || null
}

// Strona główna serwisu ma tylko pl/en, a czasopisma i monografie mają też inne języki (de, fr…) — z ich stron
// link do strony głównej prowadzi do wersji angielskiej zamiast do /de, które kończy się 404.
export const getSiteHomePath = (locale) => buildLocalizedPath(LINKS.PAGES.HOME, getRouteLocaleVariant(locale))
